import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  delay,
  filter,
  map,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { of } from "rxjs";
import { UserActions } from "../../actions/user/user.actions";
import { UserService } from "@services/user.service";
import { AuthActions } from "@store/actions/auth/auth.actions";
import { UserAdminService } from "@services/user-admin.service";
import { MsalService } from "@azure/msal-angular";
import { ROLE_GROUPS } from "../../../constants/roles";
import { selectAuthToken } from "@store/selectors/auth/auth.selectors";
import { ErrorDialogService } from "@services/error-dialog.service";
import { Organization } from "@models/organization.model";
import { User } from "@models/user.model";

function selectOrgForUser(user: User, allOrgs: Organization[]): Action[] {
  const savedOrgId = localStorage.getItem("selectedOrgId");
  const isSuperAdmin = ROLE_GROUPS.SUPER_USER.includes(user.roleId ?? "");
  const userOrgIds = user.organizationIds ?? [];

  if (!isSuperAdmin && userOrgIds.length === 0) {
    return [
      AuthActions.loginError({
        error: "No organizations assigned to your account. Please contact your administrator.",
      }),
    ];
  }

  const candidateOrgs = isSuperAdmin
    ? allOrgs
    : allOrgs.filter((org) => userOrgIds.includes(Number(org.id)));

  const enabledOrgs = candidateOrgs.filter((org) => org.statusID === 1000);

  if (enabledOrgs.length === 0) {
    if (!isSuperAdmin) {
      return [
        AuthActions.loginError({
          error: "All of your organizations are disabled. Please contact your administrator.",
        }),
      ];
    }

    let orgToLoad =
      savedOrgId && Number(savedOrgId) !== 0 ? Number(savedOrgId) : userOrgIds[0];

    if (!orgToLoad && allOrgs.length > 0) {
      orgToLoad = Number(allOrgs[0].id);
    }

    if (!orgToLoad) {
      return [
        AuthActions.loginError({
          error: "No organizations exist in the system. Please contact your system administrator.",
        }),
      ];
    }

    const loadedOrg = allOrgs.find((org) => Number(org.id) === orgToLoad);
    return [
      UserActions.setUserOrganizationId({ id: orgToLoad }),
      UserActions.allOrgsDisabledWarning({ orgName: loadedOrg?.name ?? "Organization" }),
    ];
  }

  if (savedOrgId && Number(savedOrgId) !== 0) {
    const savedOrg = candidateOrgs.find((org) => Number(org.id) === Number(savedOrgId));

    if (savedOrg?.statusID === 1000) {
      return [UserActions.setUserOrganizationId({ id: Number(savedOrgId) })];
    }

    return [
      UserActions.setUserOrganizationId({ id: Number(enabledOrgs[0].id) }),
      UserActions.orgSwitchedToEnabled({
        oldOrgName: savedOrg?.name ?? "your previously selected organization",
        newOrgName: enabledOrgs[0].name,
      }),
    ];
  }

  if (isSuperAdmin && userOrgIds.length > 0) {
    const assignedOrg = candidateOrgs.find((org) => Number(org.id) === Number(userOrgIds[0]));
    if (assignedOrg?.statusID === 1000) {
      return [UserActions.setUserOrganizationId({ id: Number(assignedOrg.id) })];
    }
  }

  return [UserActions.setUserOrganizationId({ id: Number(enabledOrgs[0].id) })];
}

@Injectable()
export class UserEffects {
  private justLoggedIn = false;

  loginSuccessFlagSetter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => {
          this.justLoggedIn = true;
        }),
      ),
    { dispatch: false },
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      switchMap(() =>
        this.store.select(selectAuthToken).pipe(
          filter((token) => !!token && token.length > 0),
          take(1),
          delay(100),
        ),
      ),
      switchMap(() => {
        const account = this.msalService.instance.getActiveAccount();

        if (!account || !account.username) {
          console.error("[UserEffects] No valid account found");
          return of(AuthActions.loginError({ error: "No valid account information" }));
        }

        return this.userService.getUserDetailsByEmail(account.username).pipe(
          switchMap((userDetails) => {
            if (!userDetails || !userDetails.id) {
              console.error("[UserEffects] User not found in database");
              return of(
                AuthActions.loginError({
                  error: "Your account was not found in the system. Please contact your administrator to set up your account.",
                }),
              );
            }

            const user = {
              ...userDetails,
              organizationIds: Array.isArray(userDetails.organizationIds)
                ? userDetails.organizationIds
                : userDetails.organizationIds
                  ? [Number(userDetails.organizationIds)]
                  : [],
            };

            if (!ROLE_GROUPS.SUPER_USER.includes(user.roleId ?? "") && user.organizationIds.length === 0) {
              console.error("[UserEffects] User has no organizations assigned");
              return of(
                AuthActions.loginError({
                  error: "No organizations assigned to your account. Please contact your administrator.",
                }),
              );
            }

            return this.userAdminService.updateUserLastLogin(Number(userDetails.id)).pipe(
              map(() => UserActions.getUserOnLoginSuccess({ user })),
              catchError(() => of(UserActions.getUserOnLoginSuccess({ user }))),
            );
          }),
          catchError((error) => {
            console.error("[UserEffects] Error fetching user details:", error);

            if (error.status === 404) {
              return of(
                AuthActions.loginError({
                  error: "Your account was not found in the system. Please contact your administrator to set up your account.",
                }),
              );
            }

            return of(
              AuthActions.loginError({
                error: "Unable to retrieve your account information. Please try again or contact your administrator.",
              }),
            );
          }),
        );
      }),
    ),
  );

  reloadUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getAzureTokenSuccess),
      filter(() => {
        if (this.justLoggedIn) {
          this.justLoggedIn = false;
          return false;
        }
        return true;
      }),
      delay(800),
      switchMap(() => {
        const account = this.msalService.instance.getActiveAccount();

        if (!account || !account.username) {
          console.error("[UserEffects] No valid account for session reload");
          return of(AuthActions.loginError({ error: "No valid account information" }));
        }

        return this.userService.getUserDetailsByEmail(account.username).pipe(
          switchMap((userDetails) => {
            if (!userDetails || !userDetails.id) {
              console.error("[UserEffects] User not found during session reload");
              return of(
                AuthActions.loginError({
                  error: "Your account was not found in the system. Please contact your administrator.",
                }),
              );
            }

            const user = {
              ...userDetails,
              organizationIds: Array.isArray(userDetails.organizationIds)
                ? userDetails.organizationIds
                : userDetails.organizationIds
                  ? [Number(userDetails.organizationIds)]
                  : [],
            };

            return of(UserActions.getUserOnLoginSuccess({ user }));
          }),
          catchError((error) => {
            console.error("[UserEffects] Error during session reload:", error);

            if (error.status === 404) {
              return of(
                AuthActions.loginError({
                  error: "Your account was not found in the system. Please contact your administrator.",
                }),
              );
            }

            return of(
              AuthActions.loginError({
                error: "Unable to retrieve your session information. Please try logging in again.",
              }),
            );
          }),
        );
      }),
    ),
  );

  selectActiveOrgOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loginOrgsLoaded),
      switchMap(({ user, orgs }) => of(...selectOrgForUser(user, orgs))),
    ),
  );

  showAllOrgsDisabledWarning$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.allOrgsDisabledWarning),
        delay(500),
        tap((action) => {
          this.errorDialogService
            .showWarning(
              `All organizations are currently disabled. You are viewing "${action.orgName}" but it is disabled. Please use the organization switcher to enable an organization.`,
              "All Organizations Disabled",
            )
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  showOrgSwitchedWarning$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.orgSwitchedToEnabled),
        delay(500),
        tap((action) => {
          this.errorDialogService
            .showWarning(
              `Your previously selected organization "${action.oldOrgName}" is now disabled. You have been switched to "${action.newOrgName}".`,
              "Organization Changed",
            )
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  saveOrgIdToLocalStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UserActions.setUserOrganizationId),
        map((action) => {
          localStorage.setItem("selectedOrgId", action.id.toString());
          return { type: "NO_ACTION" };
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private msalService: MsalService,
    private userAdminService: UserAdminService,
    private store: Store,
    private errorDialogService: ErrorDialogService,
  ) {}
}
